import {generateTokensFromObject} from '../__utils';
import {boxShadows as boxShadowsCore} from '../box-shadows';
import {coreColorTokens, coreColors} from '../colors';
import {controls} from '../controls';
import {opacities as sharedOpacities} from '../opacities';

import type {ThemeColors} from '../colors';
import type {Opacities} from '../opacities';
import type {ThemeObject} from '../theme.types';
import type {BoxShadows} from '../box-shadows';

/**
 * Refining the color of the button's background with Design acknowledgement
 * that there's a hard-coded value, and there's intention to improve the
 * tokens and controls in the near future.
 * @see https://webflow.slack.com/archives/C012XUU5EQL/p1710436375986179?thread_ts=1710192329.343909&cid=C012XUU5EQL
 * 2024-03-21
 */
const ACTION_SECONDARY_BACKGROUND_COLOR = '#efefef';

/**
 * These values are inlined into <body> styles via jade to prevent flash of
 * unstyled content (FOUC) before the theme stylesheet loads.
 *
 * - `background` — body background. Keep in sync with the `background1` token
 *   in the colors object below.
 * - `loadingTrack` — preloader progress-bar track. Keep in sync with the
 *   `background2` token in the colors object below.
 * - `loadingBackground` — preloader screen background. White in light theme,
 *   black in dark themes. Not tied to a colors-object token.
 * - `loadingBloomOpacity` — opacity for the preloader's glow stack. Subtle on
 *   light backgrounds, full on dark backgrounds.
 * - `loadingText` — preloader text color. Mirrors the `textPrimary`
 *   token so loader copy stays readable on the loader background.
 */
export const LIGHT_HTML_BACKGROUND_COLORS = {
  background: coreColors.d2.white,
  loadingTrack: coreColors.d2.gray[0],
  loadingBackground: coreColors.d2.white,
  loadingBloomOpacity: 0.1,
  loadingText: coreColors.d2.black,
};

const colors: ThemeColors = {
  actionBlackGhostBackgroundHover: 'rgba(0, 0, 0, 0.04)', // $backgroundRaised
  actionBlackGhostBackgroundSelected: coreColorTokens.d2.gray[100],
  actionPrimaryBackground: coreColorTokens.d2.brandBlue[200],
  actionPrimaryBackgroundHover: coreColorTokens.d2.brandBlue[100],
  actionPrimaryText: coreColorTokens.d2.white,
  actionPrimaryTextHover: coreColorTokens.d2.white,
  actionSecondaryBackground: `linear-gradient(180deg, rgba(255, 255, 255, 0.02) 0%, rgba(255, 255, 255, 0) 100%), ${ACTION_SECONDARY_BACKGROUND_COLOR}`,
  actionSecondaryBackgroundHover: `linear-gradient(180deg, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0.00) 100%), ${coreColorTokens.d2.gray[100]}`,
  actionSecondaryBackgroundPressed: `linear-gradient(180deg, rgba(255, 255, 255, 0.02) 0%, rgba(255, 255, 255, 0) 100%), ${ACTION_SECONDARY_BACKGROUND_COLOR}`, // deprecated - remaped to actionSecondaryBackground
  actionSecondarySelectedBackground: coreColorTokens.d2.white,
  actionSecondarySelectedBackgroundPressed: coreColorTokens.d2.white, // deprecated - remaped to actionSecondarySelectedBackground
  actionSecondaryText: coreColorTokens.d2.black,
  actionSecondaryTextHover: coreColorTokens.d2.black,
  actionSecondaryBorder: coreColorTokens.d2.blackA10,
  actionSecondaryBorderHover: coreColorTokens.d2.blackA16,
  actionWhiteGhostBackgroundHover: coreColorTokens.d2.whiteA7,
  actionWhiteGhostBackgroundSelected: coreColorTokens.d2.whiteA10,
  actionWhiteOutlineBackgroundSelected: coreColorTokens.d2.whiteA10,
  actionWhiteOutlineBorder: coreColorTokens.d2.whiteA13,
  actionWhiteOutlineBorderHover: coreColorTokens.d2.whiteA22,
  actionWhiteSolidBackgroundHover: coreColorTokens.d2.gray[300],
  /**
   * Simplifying background colors.
   * The previous background 1 ... 5 need to be consolidated (renaming callsites)
   * to the primary ... tertiary values.
   *
   * @see https://webflow.atlassian.net/browse/DS-761
   */
  backgroundPrimary: coreColorTokens.d2.white,
  backgroundPrimaryDisabled: coreColorTokens.d2.gray[100],
  backgroundSecondary: coreColorTokens.d2.gray[0],
  backgroundTertiary: coreColorTokens.d2.gray[100],
  backgroundRaised: 'rgb(0, 0, 0, 0.04)',
  backgroundHighlight: '#F6F6F6',
  background1: coreColorTokens.d2.white,
  background2: coreColorTokens.d2.gray[0],
  background3: coreColorTokens.d2.gray[100],
  background4: coreColorTokens.d2.gray[200],
  background5: coreColorTokens.d2.gray[300],
  backgroundInactive: coreColorTokens.d2.gray[0],
  backgroundInverse: coreColorTokens.d2.black,
  backgroundInverseHover: coreColorTokens.d2.black, // deprecated - remaped to backgroundInverse
  blueBackground: coreColorTokens.d2.brandBlue[200],
  blueBackgroundDisabled: coreColorTokens.d2.brandBlue[100],
  blueBackgroundHover: coreColorTokens.d2.brandBlue[100],
  blueBackgroundPressed: coreColorTokens.d2.brandBlue[200],
  blueBorder: coreColorTokens.d2.brandBlue[200],
  blueCanvas: coreColorTokens.d2.brandBlue[200],
  blueCanvasTransparent: coreColorTokens.d2.blue.A10,
  blueChart: coreColorTokens.d2.blue[400],
  blueChart2: coreColorTokens.d2.blue[100],
  blueIcon: coreColorTokens.d2.brandBlue[200],
  blueOutline: `0 0 0 2px ${coreColorTokens.d2.brandBlue.A20}`,
  blueText: coreColorTokens.d2.brandBlue[200],
  blueTransparent: coreColorTokens.d2.brandBlue.A10,
  blueTransparentHover: coreColorTokens.d2.brandBlue.A20,
  /**
   * Simplifying border colors.
   * The previous border 1 ... 4 need to be consolidated (renaming callsites)
   * to the new border values.
   *
   * @see https://webflow.atlassian.net/browse/DS-740
   */
  borderDefault: coreColorTokens.d2.gray[200],
  borderTextInput: coreColorTokens.d2.gray[300],
  borderCheckboxRadio: coreColorTokens.d2.gray[300],
  border1: coreColorTokens.d2.gray[200],
  border2: coreColorTokens.d2.gray[300],
  border3: coreColorTokens.d2.gray[300],
  border4: coreColorTokens.d2.gray[200], // deprecated - remapped to border3
  brandIconDetail: coreColorTokens.d2.gray[575],
  brandIconFocus: coreColorTokens.d2.brandBlue[200],
  brandIconOutline: coreColorTokens.d2.gray[300],
  chartSeriesTeal: '#00D2D5',
  chartSeriesOrange: '#FF9442',
  chartSeriesLightPink: '#C954CC',
  chartSeriesGreen: '#409A30',
  chartSeriesPink: '#C7006B',
  chartSeriesYellow: '#F2D72C',
  chartSeriesBlue: '#0057CB',
  chartSeriesGray: '#888888',
  closeButtonBackground: coreColorTokens.d2.blackA10,
  grayIcon: coreColorTokens.d2.gray[700],
  grayTransparent: coreColorTokens.d2.blackA7,
  grayBackground: coreColorTokens.d2.gray[300],
  grayBackgroundHover: coreColorTokens.d2.gray[300],
  grayBackgroundPressed: coreColorTokens.d2.gray[300],
  grayBorder: coreColorTokens.d2.gray[100],
  grayChart: coreColorTokens.d2.gray[500],
  grayCanvas: coreColorTokens.d2.gray[200],
  grayCanvasHover: coreColorTokens.d2.gray[300],
  grayCanvasTransparent: coreColorTokens.d2.blackA10,
  grayBackgroundTransparent: coreColorTokens.d2.blackA19,
  grayBackgroundTransparentHover: coreColorTokens.d2.blackA22,
  grayBackgroundDisabled: coreColorTokens.d2.gray[100],
  greenBackground: coreColorTokens.d2.green[300],
  greenBackgroundDisabled: coreColorTokens.d2.green[100],
  greenBackgroundHover: coreColorTokens.d2.green[200],
  greenBackgroundPressed: coreColorTokens.d2.green[300],
  greenBorder: coreColorTokens.d2.green[400],
  greenCanvas: coreColorTokens.d2.green[400],
  greenCanvasTransparent: coreColorTokens.d2.green.A10,
  greenChart: coreColorTokens.d2.green[400],
  greenIcon: coreColorTokens.d2.green[300],
  greenOutline: `0 0 0 2px ${coreColorTokens.d2.green.A20}`,
  greenText: coreColorTokens.d2.green[300],
  greenTransparent: coreColorTokens.d2.green.A10,
  greenTransparentHover: coreColorTokens.d2.green.A20,
  orangeBackground: coreColorTokens.d2.orange[300],
  orangeBackgroundDisabled: coreColorTokens.d2.orange[100],
  orangeBackgroundHover: coreColorTokens.d2.orange[200],
  orangeBackgroundPressed: coreColorTokens.d2.orange[300],
  orangeBorder: coreColorTokens.d2.orange[400],
  orangeCanvas: coreColorTokens.d2.orange[400],
  orangeCanvasTransparent: coreColorTokens.d2.orange.A10,
  orangeChart: coreColorTokens.d2.orange[400],
  orangeIcon: coreColorTokens.d2.orange[300],
  orangeText: coreColorTokens.d2.orange[300],
  orangeTransparent: coreColorTokens.d2.orange.A10,
  orangeTransparentHover: coreColorTokens.d2.orange.A20,
  pinkBackground: coreColorTokens.d2.pink[300],
  pinkBackgroundDisabled: coreColorTokens.d2.pink[100],
  pinkBackgroundHover: coreColorTokens.d2.pink[200],
  pinkBackgroundPressed: coreColorTokens.d2.pink[300],
  pinkBorder: coreColorTokens.d2.pink[400],
  pinkCanvas: coreColorTokens.d2.pink[400],
  pinkCanvasTransparent: coreColorTokens.d2.pink.A10,
  pinkChart: coreColorTokens.d2.pink[400],
  pinkIcon: coreColorTokens.d2.pink[300],
  pinkOutline: `0 0 0 2px ${coreColorTokens.d2.pink.A20}`,
  pinkText: coreColorTokens.d2.pink[300],
  pinkTransparent: coreColorTokens.d2.pink.A10,
  pinkTransparentHover: coreColorTokens.d2.pink.A20,
  // These are currently identical across all color themes and hard-coded in many places.
  // This may change as themes and presence tokens evolve.
  presenceDisabledBlue: '#CCF1F7',
  presenceDisabledGreen: '#E6F7D4',
  presenceDisabledOrange: '#F7D8C2',
  presenceDisabledPink: '#F6CAEA',
  presenceDisabledRed: '#F8CBCE',
  presenceDisabledYellow: '#FFF6CC',
  presenceCanvasBlue: '#0096B180',
  presenceCanvasGreen: '#5A9A1B80',
  presenceCanvasOrange: '#D45E0B80',
  presenceCanvasPink: '#DE2CAD80',
  presenceCanvasRed: '#EF313D80',
  presenceCanvasYellow: '#A0830080',
  presenceOutlineBlue: '#8DD9E7',
  presenceOutlineGreen: '#B3F076',
  presenceOutlineOrange: coreColorTokens.d2.orange[100],
  presenceOutlinePink: coreColorTokens.d2.pink[100],
  presenceOutlineRed: coreColorTokens.d2.red[100],
  presenceOutlineYellow: '#FFE372',
  presencePrimaryBlue: '#8DD9E7',
  presencePrimaryGreen: '#B3F076',
  presencePrimaryOrange: coreColorTokens.d2.orange[100],
  presencePrimaryPink: coreColorTokens.d2.pink[100],
  presencePrimaryRed: coreColorTokens.d2.red[100],
  presencePrimaryYellow: '#FFE372',
  presenceSecondaryBlue: '#00B8D933',
  presenceSecondaryGreen: '#80D72833',
  presenceSecondaryOrange: coreColorTokens.d2.orange.A25,
  presenceSecondaryPink: coreColorTokens.d2.pink.A25,
  presenceSecondaryRed: coreColorTokens.d2.red.A25,
  presenceSecondaryYellow: coreColorTokens.d2.yellow.A20,
  purpleBackground: coreColorTokens.d2.purple[300],
  purpleBackgroundDisabled: coreColorTokens.d2.purple[100],
  purpleBackgroundHover: coreColorTokens.d2.purple[200],
  purpleBackgroundPressed: coreColorTokens.d2.purple[300],
  purpleBorder: coreColorTokens.d2.purple[400],
  purpleCanvas: coreColorTokens.d2.purple[400],
  purpleCanvasTransparent: coreColorTokens.d2.purple.A10,
  purpleChart: coreColorTokens.d2.purple[400],
  purpleChart2: coreColorTokens.d2.purple[100],
  purpleIcon: coreColorTokens.d2.purple[300],
  purpleOutline: `0 0 0 2px ${coreColorTokens.d2.purple.A20}`,
  purpleText: coreColorTokens.d2.purple[300],
  purpleTransparent: coreColorTokens.d2.purple.A10,
  purpleTransparentHover: coreColorTokens.d2.purple.A20,
  redBackground: coreColorTokens.d2.red[300],
  redBackgroundDisabled: coreColorTokens.d2.red[100],
  redBackgroundHover: coreColorTokens.d2.red[200],
  redBackgroundPressed: coreColorTokens.d2.red[300],
  redBorder: coreColorTokens.d2.red[400],
  redCanvas: coreColorTokens.d2.red[400],
  redCanvasTransparent: coreColorTokens.d2.red.A10,
  redChart: coreColorTokens.d2.red[400],
  redIcon: coreColorTokens.d2.red[300],
  redOutline: `0 0 0 2px ${coreColorTokens.d2.red.A20}`,
  redText: coreColorTokens.d2.red[300],
  redTransparent: coreColorTokens.d2.red.A10,
  redTransparentHover: coreColorTokens.d2.red.A20,
  skeleton: coreColorTokens.d2.blackA7,
  textBlackPrimary: coreColorTokens.d2.black,
  textBlackSecondary: coreColorTokens.d2.blackA60,
  textDimmed: coreColorTokens.d2.blackA50,
  textInverse: coreColorTokens.d2.white,
  textPrimary: coreColorTokens.d2.black,
  textSecondary: coreColorTokens.d2.blackA60,
  textWhitePrimary: coreColorTokens.d2.white,
  textWhiteSecondary: coreColorTokens.d2.whiteA67,
  uiAutocompleteListBoxBackground: coreColorTokens.d2.gray[0], // $backgroundSecondary
  uiAvatarColoredTextDisabled: coreColorTokens.d2.whiteA80,
  uiAvatarDefaultTextDisabled: coreColorTokens.d2.blackA22,
  uiAvatarShadow: coreColorTokens.d2.blackA22,
  uiBackgroundActive: coreColorTokens.d2.blackA10,
  uiBackgroundHover: 'rgba(0, 0, 0, 0.04)',
  uiBadgeBackground: coreColorTokens.d2.gray[100],
  uiBadgeBackgroundHover: coreColorTokens.d2.gray[200],
  uiBadgeBackgroundPressed: coreColorTokens.d2.gray[100],
  uiBlackGhostBorder: coreColorTokens.d2.blackA10,
  uiBlackGhostBorderHover: coreColorTokens.d2.blackA16,
  uiBorderSolid: coreColorTokens.d2.gray[200],
  uiCanvasBackground: coreColorTokens.d2.gray[100],
  uiCardBackground: coreColorTokens.d2.gray[0],
  uiCheckboxRadioBorderColor: coreColorTokens.d2.gray[200], // $border2
  uiDragOverlayBackground: coreColorTokens.d2.white,
  uiInputBackground: coreColorTokens.d2.white,
  uiInputBorderColor: coreColorTokens.d2.gray[200],
  uiInputBorderColorHover: coreColorTokens.d2.gray[300],
  uiFuseControlFilter:
    'drop-shadow(0px -0.5px 0px rgba(0, 0, 0, 0.16)) drop-shadow(0px 1px 0px rgba(0, 0, 0, 0.1))',
  uiFuseControlHover: 'brightness(0.96)',
  uiFuseControlPressed: 'brightness(0.93)',
  uiMenuContentBackground: coreColorTokens.d2.gray[0], // $backgroundSecondary
  uiMenuContentBackgroundLight: coreColorTokens.d2.white, // $background1
  uiMenuDivider: coreColorTokens.d2.gray[300],
  uiModalBackdrop: coreColorTokens.d2.blackA50,
  uiNumberInputButtonIconHoverColor: coreColorTokens.d2.black,
  uiNumberInputStepperBorder: coreColorTokens.d2.gray[200],
  uiNumberInputStepperInputBorder: 'none',
  uiNumberInputValueWrapperOutline: coreColorTokens.d2.gray[200],
  uiNumberInputValueWrapperOutlineHover: coreColorTokens.d2.gray[300],
  uiPopoverBackdrop: coreColorTokens.d2.blackA50,
  uiSegmentedControlActive: coreColorTokens.d2.white,
  uiScrollbarThumb: coreColorTokens.d2.blackA10,
  uiScrollbarThumbHover: coreColorTokens.d2.blackA16,
  uiSelectBackground: coreColorTokens.d2.white,
  uiSelectBackgroundHover: coreColorTokens.d2.gray[0],
  uiSelectBorder: coreColorTokens.d2.gray[100],
  uiTableRowActiveBackground: 'rgba(0, 0, 0, 0.03)',
  uiTableZebraStripeBackground: 'rgba(0, 0, 0, 0.02)',
  yellowBackground: coreColorTokens.d2.yellow[400],
  yellowBackgroundDisabled: coreColorTokens.d2.yellow[100],
  yellowBackgroundHover: coreColorTokens.d2.yellow[100],
  yellowBackgroundPressed: coreColorTokens.d2.yellow[400],
  yellowBorder: coreColorTokens.d2.yellow[400],
  yellowCanvas: coreColorTokens.d2.yellow[400],
  yellowCanvasTransparent: coreColorTokens.d2.yellow.A10,
  yellowChart: coreColorTokens.d2.yellow[400],
  yellowIcon: coreColorTokens.d2.yellow[300],
  yellowOutline: `0 0 0 2px ${coreColorTokens.d2.yellow.A20}`,
  yellowText: coreColorTokens.d2.yellow[300],
  yellowTransparent: coreColorTokens.d2.yellow.A25,
  yellowTransparentHover: coreColorTokens.d2.yellow.A35,
};

const boxShadows: BoxShadows = {
  ...boxShadowsCore,
  actionColored:
    '0px 0.5px 1px rgba(0, 0, 0, 0.25), inset 0px 29px 23px -16px rgba(255, 255, 255, 0.04), inset 0px 0.5px 0.5px rgba(255, 255, 255, 0.2)',
  actionSecondary:
    '0px 0px 0px 0.5px rgba(0, 0, 0, 0.08), 0px -0.5px 1px 0px rgba(0, 0, 0, 0.10), 0px 1.5px 1px 0px rgba(255, 255, 255, 0.35), 0px 1px 1px 0px rgba(0, 0, 0, 0.04), 0px 0.5px 1px 0px rgba(0, 0, 0, 0.05)',
  inputInner:
    'inset 0px 16px 16px -16px rgba(0, 0, 0, 0.01), inset 0px 12px 12px -12px rgba(0, 0, 0, 0.01), inset 0px 8px 8px -8px rgba(0, 0, 0, 0.02), inset 0px 4px 4px -4px rgba(0, 0, 0, 0.02), inset 0px 3px 3px -3px rgba(0, 0, 0, 0.02), inset 0px 1px 1px -1px rgba(0, 0, 0, 0.04)',
  menu: '0px 0.5px 0.5px 0px rgba(255, 255, 255, 0.12) inset, 0px 12px 24px 8px rgba(0, 0, 0, 0.04), 0px 8px 16px 4px rgba(0, 0, 0, 0.04), 0px 4px 8px 2px rgba(0, 0, 0, 0.04), 0px 2px 6px 0px rgba(0, 0, 0, 0.04), 0px 0px 1px 0px rgba(0, 0, 0, 0.25)',
  searchableList: '0px -1px 0px 0px rgba(0, 0, 0, 0.13)',
  // No inset “pressed” shadow in light theme; dark themes override in darkDefaultTheme.
  segmentedControl: 'none !important',
  uiNumberInputDefaultButtonActive:
    '0px 0px 0px 0.5px rgba(0, 0, 0, 0.08) inset, 0px -0.5px 1px 0px rgba(0, 0, 0, 0.10) inset, 0px 1.5px 1px 0px rgba(255, 255, 255, 0.35) inset',
  uiNumberInputDefaultButtonInactive:
    '0px 0px 0px 0.5px rgba(0, 0, 0, 0.08) inset, 0px -0.5px 1px 0px rgba(0, 0, 0, 0.10) inset, 0px 1.5px 1px 0px rgba(255, 255, 255, 0.35) inset',
  uiNumberInputStepper:
    '0px 0px 0px 0.5px rgba(0, 0, 0, 0.08) inset, 0px -0.5px 1px 0px rgba(0, 0, 0, 0.10) inset, 0px 1.5px 1px 0px rgba(255, 255, 255, 0.35) inset, 0px 1px 1px 0px rgba(0, 0, 0, 0.04), 0px 0.5px 1px 0px rgba(0, 0, 0, 0.05)',
  uiSelect: 'transparent',
};

const opacities: Opacities = {
  ...sharedOpacities,
  uiFuseControlTop: 0.02,
  uiFuseControlInnerSide: 0.07,
  uiFuseControlOuterSide: 0.05,
  uiFuseControlInnerBottom: 0.055,
  uiFuseControlOuterBottom: 0.07,
};

export const lightTheme: ThemeObject = {
  boxShadows,
  colors,
  controls,
  opacities,
};

export const lightCSSVariables = generateTokensFromObject(lightTheme).setTokens;
